/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 * Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Accordion } from 'react-bootstrap';
import { JsonEditor } from 'json-edit-react';
import { getAiOp, AiOp } from '../data';
import { useTheme } from 'styled-components';
import { Table } from 'react-bootstrap';

// ProbeDetailAi displays the details of an AI probe
interface ProbeDetailAiProps {
  data: any;
}

function ProbeDetailAi({ data }: ProbeDetailAiProps) {
  const [aiOp, setAiOp] = useState<AiOp | null>(null);
  const theme: any = useTheme();

  useEffect(() => {
    async function fetchAiOp() {
      if (data?.detail?.aiOpId) {
        try {
          const opData = await getAiOp(data.detail.aiOpId);
          setAiOp(opData);
        } catch (error) {
          console.error('Error fetching AI Operation:', error);
        }
      }
    }
    
    fetchAiOp();
  }, [ data?.detail?.aiOpId ]);

  if (!data || !data.detail) {
    return (
      <Container>
        <Row>
          <Col>
            <p>No probe details available.</p>
          </Col>
        </Row>
      </Container>
    );
  }

  const { detail } = data;

  function asText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  function tail(value: any, n = 80): string {
    const txt = asText(value);
    if (!txt) {
      return '(empty)';
    }
    return txt.length > n ? `...${txt.slice(-n)}` : txt;
  }

  const textOneLiner = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    fontFamily: 'monospace',
  };

  const textBox = {
    overflow: 'auto',
    width: '100%',
    fontFamily: 'monospace',
  };

  return (
    <Container fluid style={{ backgroundColor: theme.colors.bg, color: theme.colors.ctext }}>
      <Row className="mt-3">
        <Col>
          <h4>AI Probe <span style={{ fontFamily: 'monospace' }}>{detail.aiOpId}</span></h4>
        </Col>
      </Row>
      
      {/* Status Badge */}
      <Row className="mb-3">
        <Col>
          <div>
            Status: {' '}
            {(data.pending || !data.run_ts) ? (
              <Badge bg="warning">Pending</Badge>
            ) : data.passed ? (
              <Badge bg="success">Success</Badge>
            ) : (
              <Badge bg="danger">Failed</Badge>
            )}
          </div>
          {detail.error && (
            <div className="mt-2 text-danger">
              <strong>Error:</strong> {detail.error}
            </div>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Accordion flush style={{ }} defaultActiveKey="0">
            {detail?.results && detail.results.map((res: any, idx: number) => (
              <Accordion.Item eventKey={String(idx)} key={idx}>
                <Accordion.Header>Result {idx + 1}</Accordion.Header>
                <Accordion.Body>
                  {(() => {
                    const userPrompt = asText(res?.user_prompt);
                    const systemPrompt = asText(res?.system_prompt);
                    const responseText = asText(res?.response);
                    return (
                  <Accordion flush>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <span className="fst-bold">User Prompt:&nbsp;</span> 
                        <span className="fst-italic" style={{ color: "white"}}>{tail(userPrompt)}</span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <pre>{userPrompt || '(empty)'}</pre>
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        <span className="fst-bold">System Prompt:&nbsp;</span> 
                        <span className="fst-italic" style={{ color: "white"}}>{tail(systemPrompt)}</span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <pre>{systemPrompt || '(empty)'}</pre>
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="2">
                      <Accordion.Header>
                        <span className="fst-bold">Result:&nbsp;</span> 
                        <span className="fst-italic" style={{ color: "white"}}>{tail(responseText)}</span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <pre>{responseText || '(empty)'}</pre>
                      </Accordion.Body>
                    </Accordion.Item>
                    <Accordion.Item eventKey="3">
                      <Accordion.Header>
                        <span className="fst-bold">Cost:&nbsp;</span> 
                        <span className="fst-italic me-2" style={{ color: "white"}}>$ {res?.usage?.cost?.total_cost}</span>
                        <span className="fst-bold">Tokens:&nbsp;</span> 
                        <span className="fst-italic" style={{ color: "white"}}>{res?.usage?.total_tokens}</span>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Table>
                          <tbody>
                            {res?.response?.usage?.cost && Object.entries(res.response.usage.cost).map(([key, value]) => (
                              <tr key={key}>
                                <td style={{ width: '140px' }} className="fst-bold">{key}</td>
                                <td>$ {value as any}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <Table>
                          <tbody>
                            {res?.response?.usage && Object.entries(res.response.usage)
                            .filter(([key, _]) => key !== 'cost')
                            .map(([key, value]) => (
                              <tr key={key}>
                                <td style = {{ width: '140px' }} className="fst-bold">{key}</td>
                                <td>{value as any}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                    );
                  })()}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Col>
      </Row>
    </Container>
  );
}

export default ProbeDetailAi;
